import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Leaf, ArrowLeft, Sun, Snowflake, Flower2, TreeDeciduous
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans/${planId}`);
      setPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch plan:", error);
      toast.error("Failed to load garden plan");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSeason = () => {
    const month = selectedDate.getMonth();
    if (month >= 2 && month <= 4) return "spring";
    if (month >= 5 && month <= 7) return "summer";
    if (month >= 8 && month <= 10) return "fall";
    return "winter";
  };

  const getSeasonIcon = (season) => {
    const icons = {
      spring: <Flower2 className="w-5 h-5 text-pink-500" />,
      summer: <Sun className="w-5 h-5 text-amber-500" />,
      fall: <TreeDeciduous className="w-5 h-5 text-orange-500" />,
      winter: <Snowflake className="w-5 h-5 text-blue-500" />
    };
    return icons[season];
  };

  const getSeasonColor = (season) => {
    const colors = {
      spring: "bg-pink-100 text-pink-700 border-pink-200",
      summer: "bg-amber-100 text-amber-700 border-amber-200",
      fall: "bg-orange-100 text-orange-700 border-orange-200",
      winter: "bg-blue-100 text-blue-700 border-blue-200"
    };
    return colors[season];
  };

  const currentSeason = getCurrentSeason();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Plan not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={`/dashboard/${planId}`}
              className="text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground">
                Seasonal Calendar
              </h1>
              <p className="text-sm text-muted-foreground">{plan.name}</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-primary" />
            <span className="font-serif font-bold text-primary">ROOTSPACE</span>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <Card className="card-organic lg:col-span-1">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Select Date</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border"
                data-testid="calendar"
              />
            </CardContent>
          </Card>

          {/* Season Info */}
          <Card className="card-organic lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  {getSeasonIcon(currentSeason)}
                  <span className="capitalize">{currentSeason} Tasks</span>
                </CardTitle>
                <Badge className={getSeasonColor(currentSeason)}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                {plan.seasonal_tasks?.[currentSeason]?.length > 0 ? (
                  <ul className="space-y-3">
                    {plan.seasonal_tasks[currentSeason].map((task, i) => (
                      <li 
                        key={i}
                        className={`p-4 rounded-xl border ${getSeasonColor(currentSeason)} flex items-start gap-3`}
                        data-testid={`task-${i}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-white/50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                          {i + 1}
                        </div>
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No tasks scheduled for {currentSeason}</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* All Seasons Overview */}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {['spring', 'summer', 'fall', 'winter'].map((season) => (
            <Card 
              key={season}
              className={`card-organic cursor-pointer transition-all ${
                currentSeason === season ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => {
                const monthMap = { spring: 3, summer: 6, fall: 9, winter: 0 };
                const newDate = new Date(selectedDate);
                newDate.setMonth(monthMap[season]);
                setSelectedDate(newDate);
              }}
              data-testid={`season-card-${season}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  {getSeasonIcon(season)}
                  <span className="font-serif font-semibold capitalize">{season}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {plan.seasonal_tasks?.[season]?.length || 0} tasks
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Planting Schedule by Plant */}
        <Card className="card-organic mt-8">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Planting Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(plan.plant_recommendations || []).map((plant, i) => (
                <div 
                  key={i}
                  className="p-4 border border-border rounded-xl hover:border-primary/50 transition-colors"
                  data-testid={`plant-schedule-${i}`}
                >
                  <h4 className="font-semibold mb-2">{plant.name}</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">
                      <span className="font-medium text-foreground">Plant:</span>{" "}
                      <span className="capitalize">{plant.planting_season}</span>
                    </p>
                    {plant.harvest_season && (
                      <p className="text-muted-foreground">
                        <span className="font-medium text-foreground">Harvest:</span>{" "}
                        <span className="capitalize">{plant.harvest_season}</span>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CalendarPage;
